import * as React from 'react';

import { merge } from 'libs/classname';
import Spacer from 'components/atoms/Spacer';

import cs from './Paginator.scss';

type Props = {
  currentPage: number;
  maxPage: number;
  arrowSize: 'wide' | 'normal';
  changePage: (newPage: number) => void;
};

const Paginator: React.FC<Props> = React.memo(({ currentPage, maxPage, arrowSize, changePage }) => {
  const buttonArray = computeDisplayNumbers(currentPage, maxPage);
  const onClickLeftArrow = React.useCallback(() => {
    changePage(currentPage - 1);
  }, [currentPage, changePage]);
  const onClickRightArrow = React.useCallback(() => {
    changePage(currentPage + 1);
  }, [currentPage, changePage]);

  const arrowClassName = React.useMemo(
    () => merge(cs['arrow'], arrowSize === 'wide' ? cs['wide-arrow'] : null),
    [arrowSize]
  );

  return (
    <div className={merge(cs['container'], arrowSize === 'wide' ? cs['wide-width'] : null)}>
      {currentPage !== 1 ? (
        <LeftArrow
          onClick={onClickLeftArrow}
          className={arrowClassName}
        />
      ) : (
        <Spacer size={arrowSize === 'wide' ? 50 : 30} />
      )}

      <div className={cs['page-lists']}>
        {buttonArray.map((buttonNumber: number | null, i) => (
          <PageButton
            key={i}
            isCurrentPage={buttonNumber === currentPage}
            buttonNumber={buttonNumber}
            changePage={changePage}
          />
        ))}
      </div>

      {currentPage !== maxPage ? (
        <RightArrow
          onClick={onClickRightArrow}
          className={arrowClassName}
        />
      ) : (
        <Spacer size={arrowSize === 'wide' ? 50 : 30} />
      )}
    </div>
  );
});
Paginator.displayName = 'Paginator';

type ArrowProps = {
  onClick: () => void;
  className: string;
};

const LeftArrow: React.FC<ArrowProps> = React.memo(({ onClick, className }) => (
  <div
    onClick={onClick}
    className={cs['hover']}
  >
    <div className={className}>＜</div>
  </div>
));

LeftArrow.displayName = 'LeftArrow';

const RightArrow: React.FC<ArrowProps> = React.memo(({ onClick, className }) => (
  <div
    onClick={onClick}
    className={cs['hover']}
  >
    <div className={className}>＞</div>
  </div>
));

RightArrow.displayName = 'RightArrow';

type PageButtonProps = {
  buttonNumber: number | null;
  isCurrentPage: boolean;
  changePage: (newPage: number) => void;
};

const PageButton: React.FC<PageButtonProps> = React.memo(({ buttonNumber, isCurrentPage, changePage }) => {
  const onClick = React.useCallback(() => {
    changePage(buttonNumber ?? 1);
  }, [buttonNumber, changePage]);

  return buttonNumber ? (
    <NumberButton
      number={buttonNumber}
      isCurrentPage={isCurrentPage}
      onClick={onClick}
    />
  ) : (
    <Dots />
  );
});

PageButton.displayName = 'PageButton';

type NumberButtonProps = {
  number: number;
  isCurrentPage: boolean;
  onClick: () => void;
};

const NumberButton: React.FC<NumberButtonProps> = React.memo(({ number, isCurrentPage, onClick }) => {
  const colorClass = isCurrentPage ? cs['selected'] : cs['not-selected'];
  const classNames = merge(cs['number-button'], colorClass);

  return (
    <div className={cs['box-container']}>
      <Spacer size={3} />
      <div
        onClick={onClick}
        className={cs['hover']}
      >
        <div className={classNames}>{number}</div>
      </div>
      <Spacer size={3} />
    </div>
  );
});

NumberButton.displayName = 'NumberButton';

const Dots: React.FC = () => (
  <div className={cs['box-container']}>
    <Spacer size={3} />
    <div className={cs['dots']}>...</div>
    <Spacer size={3} />
  </div>
);

// ページネーターに表示するボタンの数字を配列で返す
// nullはボタンではなくドットを表す
const computeDisplayNumbers = (currentPage: number, maxPage: number): (number | null)[] => {
  if (maxPage <= 7) return [...Array(maxPage)].map((_, i) => i + 1);

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, null, maxPage];
  }
  if (currentPage < maxPage - 3) {
    return [1, null, currentPage - 1, currentPage, currentPage + 1, null, maxPage];
  }
  if (currentPage <= maxPage) {
    return [1, null, maxPage - 4, maxPage - 3, maxPage - 2, maxPage - 1, maxPage];
  }

  throw new Error('unreachable');
};

export default Paginator;
